import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePageTitle } from '../hooks/usePageTitle'
import { leadsApi, type Lead } from '../api/leads'
import { savedViewsApi, customStatusesApi, type SavedView } from '../api/settings'
import { organizationsApi, type Organization } from '../api/contacts'
import { FilterBuilder, type FilterCondition } from '../components/FilterBuilder'
import { Pagination } from '../components/ui/Pagination'
import { usePersistedPageSize } from '../hooks/usePersistedPageSize'
import { Modal } from '../components/ui/Modal'
import { PageSpinner } from '../components/ui/Spinner'
import { EmptyState } from '../components/ui/EmptyState'
import { Avatar } from '../components/ui/Avatar'
import { DropdownSelect, type DropdownOption } from '../components/ui/DropdownSelect'
import { SourceBadge } from '../components/ui/SourceBadge'
import { SLATimer } from '../components/SLATimer'
import { CreateLeadModal } from '../components/CreateLeadModal'
import { formatDate, cn, fullName, maskEmail } from '../lib/utils'
import { Plus, Filter, Download, TrendingUp, Eye, Search, Phone, RefreshCw } from 'lucide-react'
import { startCall, decodeRt } from '../lib/sip'
import { useAuthStore } from '../store/auth'
import toast from 'react-hot-toast'
import { Controller, useForm } from 'react-hook-form'

function loadLeadsView(companyId: string) {
  try {
    const raw = localStorage.getItem(`leads-view-${companyId}`)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

export function LeadsListPage() {
  usePageTitle('Leads')
  const { companyId } = useParams<{ companyId: string }>()
  const { user } = useAuthStore()
  const isSalesUser = user?.role === 'sales_user'
  const canSeePhone = !isSalesUser || !!user?.canSeePhone
  const canSeeEmail = !isSalesUser || !!user?.canSeeEmail
  const navigate = useNavigate()
  const qc = useQueryClient()

  const saved = loadLeadsView(companyId!)

  const [page, setPage] = useState(1)
  const [limit, setLimit] = usePersistedPageSize('leads', companyId)
  const [sortField, setSortField] = useState<string>(saved?.sortField ?? 'createdAt')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>(saved?.sortOrder ?? 'DESC')
  const [filters, setFilters] = useState<FilterCondition[]>(saved?.filters ?? [])
  const [showFilters, setShowFilters] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [activeViewId, setActiveViewId] = useState<string | undefined>(saved?.activeViewId)
  const [search, setSearch] = useState<string>(saved?.search ?? '')
  const [orgFilter, setOrgFilter] = useState<string>(saved?.orgFilter ?? '')

  useEffect(() => {
    try {
      localStorage.setItem(`leads-view-${companyId}`, JSON.stringify({
        sortField, sortOrder, filters, search, orgFilter, activeViewId,
      }))
    } catch {}
  }, [companyId, sortField, sortOrder, filters, search, orgFilter, activeViewId])

  const { data: orgs } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId!, { limit: 500 }).then(r => r.data.data),
    enabled: !!companyId,
  })
  const orgMap = new Map((orgs ?? []).map((o: Organization) => [o.id, o]))


  const activeFilters: FilterCondition[] = [
    ...filters,
    ...(orgFilter ? [{ field: 'organizationId', operator: 'eq' as const, value: orgFilter }] : []),
  ]

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['leads', companyId, page, limit, sortField, sortOrder, filters, activeViewId, search, orgFilter],
    queryFn: () => leadsApi.list(companyId!, {
      page, limit, sortField, sortOrder,
      filters: activeFilters.length ? JSON.stringify(activeFilters) : undefined,
      viewId: activeViewId,
      search: search || undefined,
    }).then(r => r.data),
    enabled: !!companyId,
  })

  const { data: views } = useQuery({
    queryKey: ['views', companyId, 'lead'],
    queryFn: () => savedViewsApi.list(companyId!, 'lead').then(r => r.data),
    enabled: !!companyId,
  })

  const { data: statuses } = useQuery({
    queryKey: ['custom-statuses', companyId],
    queryFn: () => customStatusesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const applyView = (view: SavedView) => {
    setActiveViewId(view.id)
    if (view.filters?.length) setFilters(view.filters as unknown as FilterCondition[])
    if (view.sortField) setSortField(view.sortField)
    if (view.sortOrder) setSortOrder(view.sortOrder)
  }

  const exportCsv = async () => {
    const res = await leadsApi.export(companyId!, { filters: filters.length ? JSON.stringify(filters) : undefined })
    const url = URL.createObjectURL(new Blob([res.data]))
    const a = document.createElement('a'); a.href = url; a.download = 'leads.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(o => o === 'ASC' ? 'DESC' : 'ASC')
    else { setSortField(field); setSortOrder('ASC') }
  }

  const statusColor = (s: string) => {
    const status = statuses?.find(st => st.label === s)
    return status?.color ?? '#6b7280'
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <h1 className="page-title">Leads</h1>
          {data && <span className="badge badge-gray">{data.meta.total}</span>}
        </div>
        <div className="flex items-center gap-2">
          {views && views.length > 0 && (
            <div className="flex items-center gap-1 mr-2">
              <Eye className="w-4 h-4 text-gray-400" />
              <select
                value={activeViewId ?? ''}
                onChange={e => {
                  const v = views.find(v => v.id === e.target.value)
                  if (v) applyView(v); else setActiveViewId(undefined)
                }}
                className="text-sm border-none bg-transparent text-gray-600 focus:outline-none"
              >
                <option value="">All Leads</option>
                {views.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
          )}
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search leads..."
              className="input pl-8 h-8 text-sm w-48"
            />
          </div>
          {orgs && orgs.length > 0 && (
            <DropdownSelect
              value={orgFilter}
              onChange={v => { setOrgFilter(v); setPage(1) }}
              options={(orgs ?? []).map((o: Organization) => ({ value: o.id, label: o.name }))}
              placeholder="All Organizations"
              onClear={() => { setOrgFilter(''); setPage(1) }}
              clearLabel="All Organizations"
            />
          )}
          <button onClick={() => setShowFilters(o => !o)} className={cn('btn btn-secondary btn-sm', showFilters && 'bg-primary-50 text-primary-700 border-primary-200')}>
            <Filter className="w-3.5 h-3.5" /> Filters {filters.length > 0 && `(${filters.length})`}
          </button>
          {!isSalesUser && (
            <button onClick={exportCsv} className="btn btn-secondary btn-sm">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          )}
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            title="Reload leads from database"
            className="btn btn-secondary btn-sm px-2"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
          </button>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
            <Plus className="w-3.5 h-3.5" /> New Lead
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <FilterBuilder filters={filters} onChange={setFilters} refType="lead" />
          <div className="flex gap-2 mt-2">
            <button onClick={() => { setFilters([]); setPage(1) }} className="btn btn-ghost btn-sm text-gray-500">
              Clear
            </button>
            <button onClick={() => setPage(1)} className="btn btn-primary btn-sm">
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="flex-1 overflow-auto bg-white">
        {isLoading ? <PageSpinner /> : (
          <table className="table">
            <thead>
              <tr>
                {[
                  { field: 'firstName', label: 'Name' },
                  { field: 'status', label: 'Status' },
                  { field: 'source', label: 'Source' },
                  { field: 'email', label: 'Email' },
                  { field: 'phone', label: 'Phone' },
                  { field: 'assignedTo', label: 'Assigned' },
                  { field: 'createdAt', label: 'Created' },
                ].map(col => (
                  <th key={col.field} onClick={() => handleSort(col.field)} className="cursor-pointer select-none">
                    {col.label} {sortField === col.field ? (sortOrder === 'ASC' ? '↑' : '↓') : ''}
                  </th>
                ))}
                <th>SLA</th>
                <th>Organization</th>
              </tr>
            </thead>
            <tbody>
              {data?.data.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-16">
                    <EmptyState
                      icon={<TrendingUp className="w-10 h-10" />}
                      title="No leads yet"
                      description="Create your first lead or import from CSV"
                      action={<button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">New Lead</button>}
                    />
                  </td>
                </tr>
              )}
              {data?.data.map((lead: Lead) => {
                const assigneeName = lead.assignedUser
                  ? fullName(lead.assignedUser.firstName, lead.assignedUser.lastName)
                  : null
                return (
                  <tr key={lead.id} onClick={() => navigate(`/company/${companyId}/leads/${lead.id}`)} className="cursor-pointer">
                    <td className="font-medium text-gray-900">{[lead.firstName, lead.lastName].filter(Boolean).join(' ')}</td>
                    <td>
                      <span
                        className="badge text-white"
                        style={{ backgroundColor: statusColor(lead.status) }}
                      >
                        {lead.status}
                      </span>
                    </td>
                    <td><SourceBadge source={lead.source} /></td>
                    <td className="text-gray-600">
                      {canSeeEmail ? (lead.email ?? '—') : lead.email ? maskEmail(lead.email) : '—'}
                    </td>
                    {canSeePhone && lead.phone ? (
                      <td className="text-gray-600">
                        <div className="flex items-center gap-1.5">
                          <span>{lead.phone}</span>
                          <button
                            onClick={e => {
                              e.stopPropagation()
                              startCall(lead.phone!, [lead.firstName, lead.lastName].filter(Boolean).join(' '), companyId!, lead.id)
                            }}
                            title={`Call ${lead.phone}`}
                            className="text-gray-400 hover:text-green-500 transition-colors flex-shrink-0"
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    ) : isSalesUser && lead.phone ? (
                      <td className="text-gray-500">
                        <div className="flex items-center gap-1.5">
                          <span className="tracking-wider">
                            {lead.phone.length <= 6
                              ? '•'.repeat(lead.phone.length)
                              : lead.phone.slice(0, 4) + '•'.repeat(lead.phone.length - 6) + lead.phone.slice(-2)}
                          </span>
                          <button
                            onClick={async e => {
                              e.stopPropagation()
                              const res = await leadsApi.rt(companyId!, lead.id)
                              startCall(decodeRt(res.data.v), [lead.firstName, lead.lastName].filter(Boolean).join(' '), companyId!, lead.id)
                            }}
                            title="Call"
                            className="text-gray-400 hover:text-green-500 transition-colors flex-shrink-0"
                          >
                            <Phone className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    ) : (
                      <td className="text-gray-500">—</td>
                    )}
                    <td>
                      {assigneeName && (
                        <div className="flex items-center gap-1.5">
                          <Avatar name={assigneeName} size="sm" />
                          <span className="text-xs text-gray-600">{assigneeName}</span>
                        </div>
                      )}
                      {!assigneeName && <span className="text-gray-400 text-xs">Unassigned</span>}
                    </td>
                    <td className="text-gray-500 text-xs">{formatDate(lead.createdAt)}</td>
                    <td>
                      <SLATimer deadline={lead.slaDeadline} breached={lead.slaBreahed} />
                    </td>
                    <td>
                      {lead.organizationId && orgMap.has(lead.organizationId) && (() => {
                        const org = orgMap.get(lead.organizationId!)!
                        return (
                          <span
                            className="badge text-white text-xs"
                            style={{ backgroundColor: org.color ?? '#7c3aed' }}
                          >
                            {org.name}
                          </span>
                        )
                      })()}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {data && data.meta.total > 0 && (
        <Pagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={limit}
          onPageChange={setPage}
          onPageSizeChange={size => { setLimit(size); setPage(1) }}
        />
      )}

      <CreateLeadModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        companyId={companyId!}
        orgs={orgs ?? []}
        onSuccess={() => {
          setShowCreate(false)
          qc.invalidateQueries({ queryKey: ['leads', companyId] })
        }}
      />
    </div>
  )
}

